<script setup>
import MediaBankEntry from './util/media-bank-entry.vue';
import {computed, ref} from "vue";

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        require: true
    },
    content: {
        type: Object,
        required: true
    },
})

const linkedFileUrl = ref(null)

const alignmentClass = computed(() => {
    if (props.content?.alignment === 'left') {
        return 'justify-start'
    }
    if (props.content?.alignment === 'center') {
        return 'justify-center'
    }
    if (props.content?.alignment === 'right') {
        return 'justify-end'
    }
    if (props.content?.alignment === 'fullwidth') {
        return 'justify-stretch'
    }

    return 'justify-center'
})

const linkUrl = computed(() => {
    if (props.content.target_type === 'page') {
        if (! props.content.target_data?.slug) {
            return '#'
        }
        return route('landing-pages.show', props.content.target_data?.slug)
    }
    if (props.content.target_type === 'anchor' && props.content.target_data?.anchorable) {
        return props.content.target_data?.anchorable?.url
    }
    if (props.content.target_type === 'url') {
        return props.content.target_data
    }
    if (props.content.target_type === 'email') {
        return 'mailto:'+props.content.target_data
    }
    if (props.content.target_type === 'file') {
        return linkedFileUrl.value
    }

    return '#'
})

const textPaddingClasses = computed(() => {
    if (!props.content.padding) return '';

    const paddings = [];

    if (props.content.padding.top) {
        paddings.push('padding-top-'+props.content.padding.top);
    }
    if (props.content.padding.right) {
        paddings.push('padding-right-'+props.content.padding.right);
    }
    if (props.content.padding.bottom) {
        paddings.push('padding-bottom-'+props.content.padding.bottom);
    }
    if (props.content.padding.left) {
        paddings.push('padding-left-'+props.content.padding.left);
    }

    return paddings.join(' ');
})

const buttonClass = computed( () => {
    let classes = [];

    const fontSizeMap = {
        tiny: 'button-unified-text-size-tiny',
        inherit: 'button-unified-text-size-inherit',
        medium: 'button-unified-text-size-medium',
        large: 'button-unified-text-size-large',
        huge: 'button-unified-text-size-huge',
    };

    if (props.content.alignment === 'fullwidth') {
        classes.push('block w-full');
    } else {
        classes.push('inline-block');
    }

    if (props.content.color_mode === 'siteAppearance') {
        if (props.content.button_style === 'solid') {
            classes.push('border-2 border-wlPrimary bg-wlPrimary text-wlPrimaryContrast');
        }

        if (props.content.button_style === 'outline') {
            classes.push('border-2 border-wlPrimary hover:border-wlPrimary-light transition-colors text-wlPrimary');
        }

        if (props.content.button_style === 'floating') {
            classes.push('bg-wlPrimaryContrast floating-shadow text-wlPrimary hover:scale-105 hover:-translate-y-0.5 transition-transform duration-500');
        }
    }

    if (props.content.color_mode === 'custom') {
        if (props.content.button_style === 'floating') {
            classes.push('floating-shadow hover:scale-105 hover:-translate-y-0.5 transition-transform duration-500');
        }
    }


    classes.push('text-'+props.content.text_alignment)
    classes.push(fontSizeMap[props.content.font_size] || 'button-unified-text-size-inherit');

    classes.push(textPaddingClasses.value)

    if (props.content.rounding === 'siteAppearance') {
        classes.push('rounded-wl');
    } else if (props.content.rounding === 'none') {
        classes.push('rounded-none');
    } else if (props.content.rounding === 'slight') {
        classes.push('rounded-lg');
    } else if (props.content.rounding === 'medium') {
        classes.push('rounded-3xl');
    } else if (props.content.rounding === 'very') {
        classes.push('rounded-5xl');
    } else {
        classes.push('rounded-wl');
    }

    return classes.join(' ')
})

const buttonContentClass = computed(() => {
    let classes = [];

    if (props.content.icon_type !== 'none') {
        classes.push('flex items-center')
        if (props.content.icon_position === 'left') {
        }
        if (props.content.icon_position === 'top') {
            classes.push('flex-col items-center')
        }
        if (props.content.icon_position === 'right') {
            classes.push('flex-row-reverse justify-between')
        }
    }

    return classes.join(' ');
})

const buttonTextClass = computed(() => {
    return ''
})

const iconClass = computed(() => {
    let classes = [];
    if (props.content.icon_type !== 'none') {
        if (props.content.icon_position === 'left') {
            classes.push('mr-2');
        }
        if (props.content.icon_position === 'top') {

        }
        if (props.content.icon_position === 'right') {
            classes.push('ml-2');
        }

        classes.push('icon-size-'+props.content.font_size)
    }

    return classes.join(' ');
})

const hasIcon = computed(() => {
    return props.content.icon_type !== 'none';
})

const iconType = computed(() => {
    if (props.content.icon_type === 'image') {
        if (props.content.icon_source_type === 'mediaLibrary') {
            return 'media-library'
        }
    }

    if (props.content.icon_type === 'text') {
        return 'text'
    }
})

const buttonStyle = computed( () => {
    const style = {};
    if (props.content.color_mode === 'custom') {
        if (props.content.button_style === 'solid') {
            if (props.content.cssBackgroundColor) {
                style.border = '2px solid ' + props.content.cssBackgroundColor;
                style.backgroundColor = props.content.cssBackgroundColor;
            } else {
                style.border = '2px solid #efefef';
                style.backgroundColor = '#efefef';
            }
            if (props.content.cssColor) {
                style.color = props.content.cssColor;
            } else {
                style.color = '#3f3f3f';
            }
        }

        if (props.content.button_style === 'outline') {
            if (props.content.cssBackgroundColor) {
                style.border = '2px solid ' + props.content.cssBackgroundColor;
                style.backgroundColor = 'transparent';
            } else {
                style.border = '2px solid #3f3f3f';
                style.backgroundColor = 'transparent';
            }
            if (props.content.cssColor) {
                style.color = props.content.cssColor;
            } else {
                style.color = '#3f3f3f';
            }
        }

        if (props.content.button_style === 'floating') {
            if (props.content.cssBackgroundColor) {
                style.backgroundColor = props.content.cssBackgroundColor;
            } else {
                style.backgroundColor = '#efefef';
            }
            if (props.content.cssColor) {
                style.color = props.content.cssColor;
            } else {
                style.color = '#3f3f3f';
            }
        }

    }

    if (props.content.min_width) {
        style.minWidth = props.content.min_width + props.content.min_width_unit
    }

    return style;
})

const handleDataUrl = (url) => {
    axios.get(url).then(response => {
        linkedFileUrl.value = response.data.url;
    }).catch(error => {
        console.error('Error fetching data URL:', error);
    });
}

</script>
<template>
    <div>
        <div
            v-if="props.content"
            class="flex mx-auto mb-6 rounded-custom shadow-custom max-w-full"
            :class="alignmentClass"
        >
            <div
                v-if="props.content.target_type === 'file' && props.content.target_data"
                class="hidden"
            >
                <MediaBankEntry
                    class="flex justify-center text-center download-button"
                    :container-ref="containerRef"
                    :block-uuid="uuid"
                    :block-content="props.content"
                    :media-bank-entry-id="props.content.target_data"
                    property-name="target_data"
                    @data-url="handleDataUrl"
                ></MediaBankEntry>
            </div>
            <a
                :href="linkUrl"
                class="mf-link-immune"
                :class="buttonClass"
                :style="buttonStyle"
                :download="props.content.target_type === 'file' ? true : undefined"
            >
                <div :class="buttonContentClass">
                    <span
                        v-if="hasIcon"
                        :class="iconClass"
                    >
                        <MediaBankEntry
                            v-if="iconType === 'media-library'"
                            :container-ref="containerRef"
                            :block-uuid="uuid"
                            :block-content="props.content"
                            :media-bank-entry-id="props.content.media_bank_entry_id"
                            property-name="media_bank_entry_id"
                            class="aspect-square object-contain text-2xl button-unified-icon-type-image"
                        ></MediaBankEntry>

                        <span
                            v-if="iconType === 'text'"
                            class="text-2xl button-unified-icon-type-text"
                        >
                            {{ props.content.icon_text_content }}
                        </span>
                    </span >
                    <div
                        :class="buttonTextClass"
                        class="w-full"
                    >
                        <div :class="content.byline?.length > 0 ? 'mb-1' : ''">{{ content.label }}</div>
                        <div class="button-unified-byline">{{ content.byline }}</div>
                    </div>
                </div>
            </a>
        </div>
    </div>
</template>
<style scoped>

.floating-shadow {
    box-shadow: 0 8px 12px -2px rgba(0,0,0,0.15);
    transition: all 0.4s;
}

.floating-shadow:hover {
    box-shadow: 0 8px 13px -1px rgba(0,0,0,0.18);
}

.button-unified-text-size-tiny {
    line-height: 1.3em;
    font-size: 12px;
}

.button-unified-text-size-inherit {
    line-height: 1.3em;
    font-size: inherit;
}

.button-unified-text-size-medium {
    line-height: 1.3em;
    font-size: 20px;
}

.button-unified-text-size-large {
    line-height: 1.3em;
    font-size: 30px;
}

.button-unified-text-size-huge {
    line-height: 1.3em;
    font-size: 48px;
}

:deep(.button-unified-text-size-tiny img.button-unified-icon-type-image) {
    width: 12px;
    height: 12px;
}

:deep(.button-unified-text-size-inherit img.button-unified-icon-type-image) {
    width: inherit;
    height: inherit;
}

:deep(.button-unified-text-size-medium img.button-unified-icon-type-image) {
    width: 20px;
    height: 20px;
}

:deep(.button-unified-text-size-large img.button-unified-icon-type-image) {
    width: 30px;
    height: 30px;
}

:deep(.button-unified-text-size-huge img.button-unified-icon-type-image) {
    width: 48px;
    height: 48px;
}

.button-unified-text-size-tiny .button-unified-icon-type-text,
.button-unified-text-size-inherit .button-unified-icon-type-text,
.button-unified-text-size-medium .button-unified-icon-type-text {
    font-size: 1.5em;
}

.button-unified-text-size-large .button-unified-icon-type-text,
.button-unified-text-size-huge .button-unified-icon-type-text {
    font-size: 1.15em;
}

.button-unified-text-size-tiny .button-unified-byline,
.button-unified-text-size-inherit .button-unified-byline,
.button-unified-text-size-medium .button-unified-byline {
    line-height: 1.3em;
    font-size: 0.825em;
}

.button-unified-text-size-large .button-unified-byline,
.button-unified-text-size-huge .button-unified-byline {
    line-height: 1.3em;
    font-size: 0.5em;
}

.padding-top-none {
    padding-top: 0;
}
.padding-top-tiny {
    padding-top: 0.5rem;
}
.padding-top-small {
    padding-top: 1rem;
}
.padding-top-medium {
    padding-top: 2rem;
}
.padding-top-large {
    padding-top: 3rem;
}
.padding-top-huge {
    padding-top: 4rem;
}

.padding-right-none {
    padding-right: 0;
}
.padding-right-tiny {
    padding-right: 0.5rem;
}
.padding-right-small {
    padding-right: 1rem;
}
.padding-right-medium {
    padding-right: 2rem;
}
.padding-right-large {
    padding-right: 3rem;
}
.padding-right-huge {
    padding-right: 4rem;
}

.padding-bottom-none {
    padding-bottom: 0;
}
.padding-bottom-tiny {
    padding-bottom: 0.5rem;
}
.padding-bottom-small {
    padding-bottom: 1rem;
}
.padding-bottom-medium {
    padding-bottom: 2rem;
}
.padding-bottom-large {
    padding-bottom: 3rem;
}
.padding-bottom-huge {
    padding-bottom: 4rem;
}

.padding-left-none {
    padding-left: 0;
}
.padding-left-tiny {
    padding-left: 0.5rem;
}
.padding-left-small {
    padding-left: 1rem;
}
.padding-left-medium {
    padding-left: 2rem;
}
.padding-left-large {
    padding-left: 3rem;
}
.padding-left-huge {
    padding-left: 4rem;
}

@media screen and (min-width: 640px) {
    .padding-top-none {
        padding-top: 0;
    }

    .padding-top-tiny {
        padding-top: 0.5rem;
    }

    .padding-top-small {
        padding-top: 1rem;
    }

    .padding-top-medium {
        padding-top: 2rem;
    }

    .padding-top-large {
        padding-top: 4rem;
    }

    .padding-top-huge {
        padding-top: 8rem;
    }

    .padding-right-none {
        padding-right: 0;
    }

    .padding-right-tiny {
        padding-right: 0.5rem;
    }

    .padding-right-small {
        padding-right: 1rem;
    }

    .padding-right-medium {
        padding-right: 2rem;
    }

    .padding-right-large {
        padding-right: 4rem;
    }

    .padding-right-huge {
        padding-right: 8rem;
    }

    .padding-bottom-none {
        padding-bottom: 0;
    }

    .padding-bottom-tiny {
        padding-bottom: 0.5rem;
    }

    .padding-bottom-small {
        padding-bottom: 1rem;
    }

    .padding-bottom-medium {
        padding-bottom: 2rem;
    }

    .padding-bottom-large {
        padding-bottom: 4rem;
    }

    .padding-bottom-huge {
        padding-bottom: 8rem;
    }

    .padding-left-none {
        padding-left: 0;
    }

    .padding-left-tiny {
        padding-left: 0.5rem;
    }

    .padding-left-small {
        padding-left: 1rem;
    }

    .padding-left-medium {
        padding-left: 2rem;
    }

    .padding-left-large {
        padding-left: 4rem;
    }

    .padding-left-huge {
        padding-left: 8rem;
    }
}
</style>
